import { FoodsIcon } from "@foodpilot/foods";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRecalculateActionPlan } from "src/api/client-api/ActionPlan/ActionPlan/useRecalculateActionPlan";
import { useDeleteActionPlanAction } from "src/api/client-api/ActionPlan/ActionPlanAction/useDeleteActionPlanAction";
import { SavingCloudStatus } from "src/components/Loader/SavingCloudStatus";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { useSnackbar } from "src/utils/useSnackbar";

export type CustomHeaderProps = {
    title: string;

    actionId: string | undefined;
    actionPlanId: string | undefined;
};

export const CustomHeader = (props: CustomHeaderProps) => {
    const { title, actionId, actionPlanId } = props;
    const { savingStatus, urls } = useActionsContext();

    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const recalculate = useRecalculateActionPlan(actionPlanId);
    const deleteActionPlanAction = useDeleteActionPlanAction();
    const snackbar = useSnackbar();

    return (
        <Stack
            flexDirection={"row"}
            gap={theme.spacing(4)}
            sx={{
                justifyContent: "space-between",
                alignItems: "center",
                padding: theme.spacing(3, 4, 3, 4),
            }}
        >
            <Stack>
                <Typography variant="h3">{title}</Typography>
                <Typography variant="small-medium">{t("action-plan.action.info")}</Typography>
            </Stack>
            <Box sx={{ display: "flex", gap: theme.spacing(2), alignItems: "center" }}>
                <SavingCloudStatus
                    isSaving={savingStatus.isSaving}
                    isError={savingStatus.isError}
                    textOptions={{
                        syncingMessage: "",
                        errorDefaultMessage: "",
                        savingDefaultMessage: "",
                    }}
                />
                <Button
                    variant="ternary"
                    disabled={savingStatus.isSaving}
                    onClick={() => {
                        savingStatus.setSaving(true);
                        const payload = {
                            actionPlanActionId: actionId,
                            actionPlanId,
                        };
                        deleteActionPlanAction.mutate(payload, {
                            onSuccess: () => {
                                recalculate.mutate(null);

                                savingStatus.setSaving(false);
                                const url = urls.planning();
                                navigate(url);
                            },
                            onSettled: () => {
                                savingStatus.setSaving(false);
                            },
                        });
                    }}
                >
                    {t("actionPlan.deleteActionPost")}
                </Button>
                <Button
                    variant="validate"
                    onClick={() => {
                        savingStatus.setSaving(true);

                        recalculate.mutate(null, {
                            onSuccess: () => {
                                savingStatus.setSaving(false);
                                const url = urls.planning();
                                navigate(url);
                            },
                            onSettled: () => {
                                savingStatus.setSaving(false);
                            },
                        });
                    }}
                    disabled={savingStatus.isSaving}
                    startIcon={<FoodsIcon size={2} icon={"check"} />}
                >
                    {t("actionPlan.goBackToList")}
                </Button>
            </Box>
        </Stack>
    );
};
